/**
 * Utilities for speech transcription correction and processing
 * Shared across interview components
 */

// Common word corrections for company names and platform-specific terms
export const COMMON_WORD_CORRECTIONS = {
  // Company variations
  google: 'Google',
  meta: 'Meta',
  facebook: 'Meta',
  microsoft: 'Microsoft',
  amazon: 'Amazon',
  apple: 'Apple',
  netflix: 'Netflix',
  tesla: 'Tesla',
  salesforce: 'Salesforce',
  adobe: 'Adobe',
  uber: 'Uber',
  airbnb: 'Airbnb',
  spotify: 'Spotify',
  stripe: 'Stripe',
  shopify: 'Shopify',
  palantir: 'Palantir',
  snowflake: 'Snowflake',
  datadog: 'Datadog',
  atlassian: 'Atlassian',
  slack: 'Slack',
  zoom: 'Zoom',

  // InternHub variations
  'intern hub': 'InternHub',
  internhub: 'InternHub',
  'internal hub': 'InternHub',
  'internet hub': 'InternHub',
};

// Behavioral interview specific corrections
export const BEHAVIORAL_WORD_CORRECTIONS = {
  // STAR method variations
  'star method': 'STAR method',
  star: 'STAR',
  'situation task action result': 'Situation, Task, Action, Result',

  // Common behavioral interview terms
  leadership: 'leadership',
  teamwork: 'teamwork',
  'team work': 'teamwork',
  'conflict resolution': 'conflict resolution',
  'problem solving': 'problem solving',
  'problem-solving': 'problem-solving',
  'time management': 'time management',
  'project management': 'project management',
  stakeholder: 'stakeholder',
  stakeholders: 'stakeholders',
  deadline: 'deadline',
  deadlines: 'deadlines',
  priority: 'priority',
  priorities: 'priorities',
  initiative: 'initiative',
  proactive: 'proactive',
  reactive: 'reactive',
  feedback: 'feedback',
  'constructive feedback': 'constructive feedback',
  mentor: 'mentor',
  mentoring: 'mentoring',
  collaboration: 'collaboration',
  communicate: 'communicate',
  communication: 'communication',
  presentation: 'presentation',
  presentations: 'presentations',

  // Technical role terms that come up in behavioral contexts
  'software engineer': 'software engineer',
  'software engineering': 'software engineering',
  'full stack': 'full stack',
  'full-stack': 'full-stack',
  'front end': 'front end',
  'front-end': 'front-end',
  'back end': 'back end',
  'back-end': 'back-end',
  internship: 'internship',
  intern: 'intern',
  'code review': 'code review',
  'code reviews': 'code reviews',
  'pull request': 'pull request',
  'pull requests': 'pull requests',
  agile: 'Agile',
  scrum: 'Scrum',
  sprint: 'sprint',
  sprints: 'sprints',
  'stand up': 'stand-up',
  standup: 'stand-up',
  retrospective: 'retrospective',
  retro: 'retrospective',
};

// Technical interview specific corrections (for MockInterview.js)
export const TECHNICAL_WORD_CORRECTIONS = {
  // LeetCode variations
  'leet code': 'LeetCode',
  leetcode: 'LeetCode',
  'lead code': 'LeetCode',
  'elite code': 'LeetCode',
  'leak code': 'LeetCode',

  // Programming terms
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  python: 'Python',
  'c plus plus': 'C++',
  'c++': 'C++',
  java: 'Java',
  golang: 'Go',
  'go lang': 'Go',
  react: 'React',
  nodejs: 'Node.js',
  'node js': 'Node.js',

  // Data structures
  'hash map': 'HashMap',
  hashmap: 'HashMap',
  'hash table': 'hash table',
  'linked list': 'linked list',
  'binary tree': 'binary tree',
  'binary search': 'binary search',
  'dynamic programming': 'dynamic programming',
  dp: 'DP',
  'big o': 'Big O',
  'time complexity': 'time complexity',
  'space complexity': 'space complexity',
};

/**
 * Correct transcribed text with word replacements and capitalization
 * @param {string} text - Raw transcribed text
 * @param {Object} additionalCorrections - Extra corrections to apply
 * @returns {string} Corrected text
 */
/** Escape regex metacharacters so keys like "c++" can't build an invalid pattern. */
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Build a match pattern for one correction key.
 *
 * `\b` is only meaningful next to a word character. A key like "c++" ends in
 * `+`, so a trailing `\b` requires the NEXT character to be a word character —
 * meaning `\bc\+\+\b` matches "c++x" but not "c++ " or "c++" at end of input,
 * which is exactly backwards. Anchor each side only where it makes sense.
 */
const buildKeyPattern = (key) => {
  const escaped = escapeRegExp(key);
  const prefix = /^\w/.test(key) ? '\\b' : '';
  const suffix = /\w$/.test(key) ? '\\b' : '';
  return new RegExp(`${prefix}${escaped}${suffix}`, 'gi');
};

export const correctTranscription = (text, additionalCorrections = {}) => {
  const allCorrections = {
    ...COMMON_WORD_CORRECTIONS,
    ...additionalCorrections,
  };

  // NOTE: the source text is deliberately NOT lowercased.
  //
  // It used to be, with sentence starts re-capitalized afterwards. That
  // destroyed every proper noun and standalone "I" the speaker used: "I
  // interned at Stanford with Dr. Chen on Kubernetes" came back as "i interned
  // at stanford with dr. Chen on kubernetes". This text is what the candidate
  // reads back, what is sent to Claude for grading, and what gets exported to
  // the Q&A bank, so the damage was visible in all three.
  let correctedText = text;

  // Replace known variations with correct forms. The match is case-insensitive
  // but only the matched span is rewritten; everything else keeps its casing.
  Object.entries(allCorrections).forEach(([incorrect, correct]) => {
    // Skip only EXACT identities. Comparing case-insensitively here would skip
    // every pure-capitalization mapping ("leetcode" -> "LeetCode",
    // "python" -> "Python"), which is the single most common kind of entry in
    // these maps — speech recognition emits lowercase, and fixing that casing
    // is most of what this function is for.
    if (incorrect === correct) return;
    // Function replacement, so a `$&` or `$1` appearing in a caller-supplied
    // correction value is inserted literally rather than interpreted.
    correctedText = correctedText.replace(buildKeyPattern(incorrect), () => correct);
  });

  // Capitalize the first letter of each sentence (speech recognition often
  // returns everything lowercase to begin with).
  correctedText = correctedText.replace(/(^\s*\w|[.!?]\s+\w)/g, (letter) => letter.toUpperCase());

  return correctedText;
};

/**
 * Get combined corrections for behavioral interviews
 * @returns {Object} Merged corrections object
 */
export const getBehavioralCorrections = () => ({
  ...COMMON_WORD_CORRECTIONS,
  ...BEHAVIORAL_WORD_CORRECTIONS,
});

/**
 * Get combined corrections for technical interviews
 * @returns {Object} Merged corrections object
 */
export const getTechnicalCorrections = () => ({
  ...COMMON_WORD_CORRECTIONS,
  ...TECHNICAL_WORD_CORRECTIONS,
});
